import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { createAutomation as createAutomationService } from '../services/createAutomation.service';
import { deleteAutomation as deleteAutomationService } from '../services/deleteAutomation.service';
import { getAutomationProjects } from '../services/getAutomationProjects.service';
import { getAutomations } from '../services/getAutomations.service';
import { updateAutomation as updateAutomationService } from '../services/updateAutomation.service';

/**
 * The company's scheduled AI tasks, as the settings screen sees them.
 *
 * One list, keyed by nothing: the screen shows one company at a time, the
 * one the assistant charges, so a second company's list replaces the first
 * rather than living beside it.
 *
 * Every write goes to the API and the row the API returns replaces the local
 * one. Nothing is decided here, so a refused change leaves the switch where
 * the server says it is rather than where the click left it.
 */

export const loadAutomations = createAsyncThunk(
  'aiAutomations/load',
  async ({ businessId }) => getAutomations({ businessId })
);

export const saveAutomation = createAsyncThunk(
  'aiAutomations/save',
  async ({ automationId, enabled, schedule, customInstructions }) =>
    updateAutomationService({
      automationId,
      enabled,
      schedule,
      customInstructions,
    })
);

export const loadAutomationProjects = createAsyncThunk(
  'aiAutomations/loadProjects',
  async ({ businessId }) => getAutomationProjects({ businessId })
);

/**
 * One task. Several chosen sites become several of these, because a task
 * covers one site so that each can carry its own instructions.
 */
export const addAutomation = createAsyncThunk(
  'aiAutomations/add',
  async ({
    businessId,
    kind,
    schedule,
    projectSelection,
    customInstructions,
  }) =>
    createAutomationService({
      businessId,
      kind,
      schedule,
      projectSelection,
      customInstructions,
    })
);

export const removeAutomation = createAsyncThunk(
  'aiAutomations/remove',
  async ({ automationId }) => {
    await deleteAutomationService({ automationId });
    return { automationId };
  }
);

const initialState = {
  automations: [],
  /** The company's sites a task could be pointed at; loaded when one is made. */
  projects: [],
  projectsLoading: false,
  creating: false,
  createError: null,
  /** Rows the server could not read. Worth saying out loud; see the API. */
  unreadableRows: 0,
  loading: false,
  /** The id being written, so one card can show a spinner and the rest stay live. */
  saving: null,
  loadError: null,
  saveError: null,
};

const replaceRow = (state, automation) => {
  if (!automation?.id) return;

  state.automations = state.automations.map((row) =>
    row.id === automation.id ? automation : row
  );
};

export const aiAutomationsSlice = createSlice({
  name: 'aiAutomations',
  initialState,
  reducers: {
    clearAutomationErrors(state) {
      state.loadError = null;
      state.saveError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadAutomations.pending, (state) => {
        state.loading = true;
        state.loadError = null;
      })
      .addCase(loadAutomations.fulfilled, (state, action) => {
        state.loading = false;
        state.automations = action.payload?.automations ?? [];
        state.unreadableRows = action.payload?.unreadableRows ?? 0;
      })
      .addCase(loadAutomations.rejected, (state, action) => {
        state.loading = false;
        state.automations = [];
        state.loadError = action.error?.message ?? 'failed';
      })
      .addCase(saveAutomation.pending, (state, action) => {
        state.saving = action.meta.arg?.automationId ?? null;
        state.saveError = null;
      })
      .addCase(saveAutomation.fulfilled, (state, action) => {
        state.saving = null;
        replaceRow(state, action.payload?.automation);
      })
      .addCase(saveAutomation.rejected, (state, action) => {
        state.saving = null;
        state.saveError = action.error?.message ?? 'failed';
      })
      .addCase(loadAutomationProjects.pending, (state) => {
        state.projectsLoading = true;
      })
      .addCase(loadAutomationProjects.fulfilled, (state, action) => {
        state.projectsLoading = false;
        state.projects = action.payload?.projects ?? [];
      })
      .addCase(loadAutomationProjects.rejected, (state) => {
        state.projectsLoading = false;
        state.projects = [];
      })
      .addCase(addAutomation.pending, (state) => {
        state.creating = true;
        state.createError = null;
      })
      .addCase(addAutomation.fulfilled, (state, action) => {
        state.creating = false;
        if (action.payload?.automation) {
          state.automations = [...state.automations, action.payload.automation];
        }
      })
      .addCase(addAutomation.rejected, (state, action) => {
        state.creating = false;
        state.createError = action.error?.message ?? 'failed';
      })
      .addCase(removeAutomation.pending, (state, action) => {
        state.saving = action.meta.arg?.automationId ?? null;
      })
      .addCase(removeAutomation.fulfilled, (state, action) => {
        state.saving = null;
        state.automations = state.automations.filter(
          (row) => row.id !== action.payload?.automationId
        );
      })
      .addCase(removeAutomation.rejected, (state, action) => {
        state.saving = null;
        state.saveError = action.error?.message ?? 'failed';
      });
  },
});

export const { clearAutomationErrors } = aiAutomationsSlice.actions;

export const selectAutomations = (state) => state.aiAutomations.automations;
export const selectAutomationsLoading = (state) => state.aiAutomations.loading;
export const selectSavingAutomationId = (state) => state.aiAutomations.saving;
export const selectAutomationsLoadError = (state) =>
  state.aiAutomations.loadError;
export const selectAutomationsSaveError = (state) =>
  state.aiAutomations.saveError;
export const selectUnreadableAutomationRows = (state) =>
  state.aiAutomations.unreadableRows;
export const selectAutomationProjects = (state) => state.aiAutomations.projects;
export const selectAutomationProjectsLoading = (state) =>
  state.aiAutomations.projectsLoading;
export const selectCreatingAutomation = (state) => state.aiAutomations.creating;
export const selectAutomationCreateError = (state) =>
  state.aiAutomations.createError;

export default aiAutomationsSlice.reducer;
